import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import css from './SectionCarousel.module.css';

// Valid Console category IDs (source of truth)
const VALID_CATEGORY_IDS = [
  'tree_service',
  'brush_clearing',
  'chipping',
  'debris',
  'chipping_debris',
  'dsc',
  'frl',
  'grazing',
  'hh',
  'heavy_machinery',
  'consultation',
];

// Maps category IDs to their informational page URLs
const CATEGORY_INFO_ROUTES = {
  tree_service: '/services/tree-trimming-removal',
  brush_clearing: '/services/brush-clearing',
  // Old blockIds still live in Console until the card swap — send them to the merged page
  chipping: '/services/chipping-debris-removal',
  debris: '/services/chipping-debris-removal',
  chipping_debris: '/services/chipping-debris-removal',
  dsc: '/services/defensible-space-clearing',
  frl: '/services/fire-resistant-landscaping',
  grazing: '/services/grazing',
  hh: '/services/home-hardening',
  heavy_machinery: '/services/heavy-machinery',
  consultation: '/services/consultation',
};

/**
 * Get route from block using explicit blockId.
 * blockId must be set to a valid Console category ID in the CMS.
 * All service categories route to their informational page first.
 */
const getCategoryRoute = (block) => {
  const blockId = block?.blockId;

  if (!blockId) {
    console.warn('[SectionCarousel] Block missing blockId:', block);
    return '/request';
  }

  // Route service categories to their info page
  if (CATEGORY_INFO_ROUTES[blockId]) {
    const route = CATEGORY_INFO_ROUTES[blockId];
    console.log('[SectionCarousel] Routing to:', route, '(blockId:', blockId, ')');
    return route;
  }

  // Special case for resources
  if (blockId === 'resources') {
    console.log('[SectionCarousel] Routing to: /p/resources');
    return '/p/resources';
  }

  console.warn(`[SectionCarousel] blockId "${blockId}" is not a valid category. Valid IDs:`, VALID_CATEGORY_IDS);
  return '/request';
};

/**
 * Section component that shows blocks in a flat horizontal scroll row.
 * User-controlled scrolling only (no autoplay).
 * Clicking a card navigates to the request flow with that category.
 */
const SectionCarousel = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    numColumns = 1,
    title,
    description,
    appearance,
    callToAction,
    blocks = [],
    options,
  } = props;

  const history = useHistory();
  const hasBlocks = blocks?.length > 0;
  const containerRef = useRef(null);
  const [cardsVisible, setCardsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCardsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // If external mapping has been included for fields
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);

  const handleCardClick = (block) => {
    const route = getCategoryRoute(block);
    history.push(route);
  };

  const handleKeyDown = (e, block) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick(block);
    }
  };

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
      disableReveal
    >
      {hasHeaderFields ? (
        <header className={defaultClasses.sectionDetails}>
          <Field data={title} className={defaultClasses.title} options={fieldOptions} />
          <Field data={description} className={defaultClasses.description} options={fieldOptions} />
          <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
        </header>
      ) : null}

      {hasBlocks ? (
        <div className={css.scrollContainer} ref={containerRef}>
          <div
            className={css.scrollRow}
            role="list"
            aria-label="Service categories"
          >
            {blocks.map((block, index) => {
              const blockId = block.blockId || `${sectionId}-block-${index + 1}`;

              return (
                <div
                  key={blockId}
                  className={classNames(css.card, { [css.cardVisible]: cardsVisible })}
                  onClick={() => handleCardClick(block)}
                  onKeyDown={(e) => handleKeyDown(e, block)}
                  role="button"
                  tabIndex={0}
                  aria-label={`Select ${block.blockName || 'category'}`}
                >
                  <BlockBuilder
                    blocks={[block]}
                    sectionId={sectionId}
                    responsiveImageSizes="300px"
                    options={options}
                    rootClassName={css.blockContent}
                    mediaClassName={css.blockMedia}
                    textClassName={css.blockText}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </SectionContainer>
  );
};

export default SectionCarousel;
